import {Injectable} from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {Observable, from, of, EMPTY, throwError} from 'rxjs';
import {catchError, mergeMap, tap} from 'rxjs/operators';
import {CacheStoreService, CacheEntry} from '../services/cache-store.service';
import {CachePolicyService} from '../services/cache-policy.service';
import {CacheRefreshService} from '../services/cache-refresh.service';
import {CacheStatusService} from '../services/cache-status.service';
import {TokenDataProviderService} from '../services/token-data-provider.service';
import {CacheTagService} from '../services/cache-tag.service';
import {environment} from "../../../environments/environment";

@Injectable()
export class CacheHttpInterceptor implements HttpInterceptor {
  private readonly cacheEnabled = environment.httpCacheEnabled;

  constructor(
    private readonly cacheStore: CacheStoreService,
    private readonly cachePolicy: CachePolicyService,
    private readonly cacheRefresh: CacheRefreshService,
    private readonly cacheStatus: CacheStatusService,
    private readonly tokenDataProvider: TokenDataProviderService,
    private readonly cacheTags: CacheTagService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.cacheEnabled) {
      return next.handle(this.stripCacheHeaders(req));
    }

    this.cacheStore.touch();

    if (this.shouldBypassMediaCdn(req.url)) {
      return next.handle(req);
    }

    if (req.responseType === 'blob') {
      return next.handle(req);
    }

    if (!this.cachePolicy.isCacheable(req.url)) {
      return next.handle(req);
    }

    if (req.method !== 'GET') {
      return next.handle(this.stripCacheHeaders(req)).pipe(
        tap((event) => {
          if (event instanceof HttpResponse) {
            const tags = this.mergeTags(
              this.deriveTags(req.url),
              this.getHeaderTags(req, 'x-cache-invalidate')
            );
            if (tags.length) {
              const keys = this.cacheTags.invalidateTags(tags);
              keys.forEach((key) => {
                void this.cacheStore.deleteByKey(key);
              });
              this.buildPrefixesFromTags(tags).forEach((prefix) => {
                void this.cacheStore.deleteByPrefix(prefix);
              });
            }
            void this.cacheStore.deleteByPrefix(this.getResourcePrefix(req.url));
          }
        })
      );
    }

    if (req.headers.has('x-cache-bypass')) {
      return next.handle(req);
    }

    if (req.headers.get('Cache-Control')?.includes('no-store')) {
      return next.handle(req);
    }

    const cacheKey = this.buildKey(req);
    const ttlMs = this.cachePolicy.getTtlMs(req.url);
    const isOffline = this.cacheStore.isOffline();
    const refreshMode = this.cacheRefresh.shouldBypass() || req.headers.has('x-cache-refresh');
    const softRefresh = req.headers.has('x-cache-soft-refresh');
    const tags = this.mergeTags(
      this.deriveTags(req.url),
      this.getHeaderTags(req, 'x-cache-tags')
    );

    if (refreshMode) {
      return this.networkAndCache(req, next, cacheKey, ttlMs, false, tags);
    }

    const allowStale = !this.isStaleSensitive(req.urlWithParams);
    return from(this.cacheStore.get(cacheKey)).pipe(
      mergeMap((entry) => {
        const isFresh = this.isEntryFresh(entry);
        const cached = this.toCachedResponse(entry, allowStale);
        if (softRefresh) {
          if (cached) {
            this.cacheStatus.notify(req.urlWithParams, isFresh ? 'HIT' : 'STALE');
            if (!isOffline) {
              this.networkAndCache(req, next, cacheKey, ttlMs, true, tags).subscribe();
            }
            return of(cached);
          }
          return this.networkAndCache(req, next, cacheKey, ttlMs, false, tags);
        }
        if (cached) {
          if (isOffline) {
            this.cacheStatus.notify(req.urlWithParams, 'STALE');
            return of(cached);
          }
          if (isFresh) {
            this.cacheStatus.notify(req.urlWithParams, 'HIT');
            return of(cached);
          }
          this.cacheStatus.notify(req.urlWithParams, 'STALE');
          this.networkAndCache(req, next, cacheKey, ttlMs, true, tags).subscribe();
          return of(cached);
        }
        if (isOffline) {
          this.cacheStatus.notify(req.urlWithParams, 'OFFLINE');
          return throwError(() => this.buildOfflineError(req));
        }
        return this.networkAndCache(req, next, cacheKey, ttlMs, false, tags);
      })
    );
  }

  private networkAndCache(
    req: HttpRequest<any>,
    next: HttpHandler,
    cacheKey: string,
    ttlMs: number,
    suppressError: boolean,
    tags: string[]
  ): Observable<HttpEvent<any>> {
    return next.handle(this.stripCacheHeaders(req)).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.cacheStatus.notify(req.urlWithParams, 'NETWORK');
          const entry: CacheEntry = {
            body: event.body,
            status: event.status,
            statusText: event.statusText,
            headers: this.headersToObject(event.headers),
            url: event.url || req.url,
            updatedAt: Date.now(),
            ttlMs
          };
          this.cacheStore.set(cacheKey, entry);
          this.cacheTags.indexKey(cacheKey, tags);
        }
      }),
      catchError((error) => {
        return suppressError ? EMPTY : throwError(() => error);
      })
    );
  }

  private toCachedResponse(entry: CacheEntry | null, allowStale: boolean): HttpResponse<any> | null {
    if (!entry) {
      return null;
    }
    const isFresh = this.isEntryFresh(entry);
    if (!allowStale && !isFresh) {
      return null;
    }
    const headers = new HttpHeaders(entry.headers || {}).set('x-cache', isFresh ? 'HIT' : 'STALE');
    return new HttpResponse({
      body: entry.body,
      status: entry.status,
      statusText: entry.statusText,
      url: entry.url,
      headers
    });
  }

  private isEntryFresh(entry: CacheEntry | null): boolean {
    if (!entry) {
      return false;
    }
    if (!entry.ttlMs) {
      return true;
    }
    return Date.now() - entry.updatedAt <= entry.ttlMs;
  }

  private buildKey(req: HttpRequest<any>): string {
    const language = req.headers.get('Accept-Language') || 'default';
    const auth = req.headers.get('Authorization') || 'anon';
    const userKey = this.getUserCacheKey();
    const normalizedUrl = this.normalizeUrlForKey(req.urlWithParams);
    return `${req.method}:${normalizedUrl}:${language}:${auth}:${userKey}`;
  }

  private normalizeUrlForKey(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      const params = new URLSearchParams();
      const entries = Array.from(parsed.searchParams.entries())
        .map(([key, value]) => [key, this.normalizeParamValue(key, value)] as [string, string])
        .sort((a, b) => (a[0] + a[1]).localeCompare(b[0] + b[1]));
      entries.forEach(([key, value]) => params.append(key, value));
      const query = params.toString();
      return query ? `${parsed.origin}${parsed.pathname}?${query}` : `${parsed.origin}${parsed.pathname}`;
    } catch {
      return url;
    }
  }

  private normalizeParamValue(key: string, value: string): string {
    const normalizedKey = key.toLowerCase();
    if (normalizedKey.includes('featuredat')) {
      return 'now';
    }
    if (this.isCoordinateParam(key)) {
      const parts = value.split(',');
      if (parts.length >= 2) {
        const lat = this.roundCoord(parts[0]);
        const lng = this.roundCoord(parts[1]);
        const rest = parts.slice(2);
        return [lat, lng, ...rest].join(',');
      }
    }
    return value;
  }

  private isStaleSensitive(url: string): boolean {
    return url.toLowerCase().includes('featuredat');
  }

  private isCoordinateParam(key: string): boolean {
    return key.includes('order[distance]') || key.includes('addresses.coordinates') || key.includes('info.city.coordinates');
  }

  private roundCoord(value: string): string {
    const num = Number(value);
    if (Number.isNaN(num)) {
      return value;
    }
    return num.toFixed(3);
  }

  private stripCacheHeaders(req: HttpRequest<any>): HttpRequest<any> {
    const headersToStrip = ['x-cache-refresh', 'x-cache-soft-refresh', 'x-cache-invalidate', 'x-cache-tags'];
    let headers = req.headers;
    headersToStrip.forEach((header) => {
      if (headers.has(header)) {
        headers = headers.delete(header);
      }
    });
    return headers === req.headers ? req : req.clone({headers});
  }

  private shouldBypassMediaCdn(url: string): boolean {
    try {
      const parsed = new URL(url, window.location.origin);
      const host = parsed.hostname.toLowerCase();

      return host === 'media.watchthisgarage.com'
        || host.endsWith('.watchthisgarage.com') && host.startsWith('media.');
    } catch {
      return false;
    }
  }

  private deriveTags(url: string): string[] {
    try {
      const parsed = new URL(url, window.location.origin);
      const parts = parsed.pathname.split('/').filter(Boolean);
      const apiIndex = parts.indexOf('api');
      if (apiIndex === -1 || parts.length <= apiIndex + 1) {
        return [];
      }
      const resource = parts[apiIndex + 1];
      const id = parts[apiIndex + 2];
      const subresource = parts[apiIndex + 3];
      const subId = parts[apiIndex + 4];
      return this.mergeTags(
        [resource, id ? `${resource}:${id}` : ''],
        [subresource || '', subId && subresource ? `${subresource}:${subId}` : ''],
        [id && subresource ? `${resource}:${id}:${subresource}` : '']
      );
    } catch {
      return [];
    }
  }

  private getHeaderTags(req: HttpRequest<any>, header: string): string[] {
    const raw = req.headers.get(header);
    if (!raw) {
      return [];
    }
    return raw.split(',').map((value) => value.trim()).filter(Boolean);
  }

  private mergeTags(...tagGroups: string[][]): string[] {
    const tags: string[] = [];
    tagGroups.forEach((group) => {
      group.forEach((tag) => {
        if (tag && !tags.includes(tag)) {
          tags.push(tag);
        }
      });
    });
    return tags;
  }

  private buildPrefixesFromTags(tags: string[]): string[] {
    const prefixes = new Set<string>();
    tags.forEach((tag) => {
      const trimmed = tag.trim();
      if (!trimmed) {
        return;
      }
      if (trimmed.includes(':')) {
        const parts = trimmed.split(':');
        if (parts.length === 2) {
          const [resource, id] = parts;
          if (resource && id) {
            prefixes.add(`/api/${resource}/${id}`);
          }
          return;
        }
        if (parts.length === 3) {
          const [resource, id, subresource] = parts;
          if (resource && id && subresource) {
            prefixes.add(`/api/${resource}/${id}/${subresource}`);
          }
          return;
        }
        return;
      }
      prefixes.add(`/api/${trimmed}`);
    });
    return Array.from(prefixes);
  }

  private getResourcePrefix(url: string): string {
    const base = url.split('?')[0];
    const parts = base.split('/').filter(Boolean);
    const apiIndex = parts.indexOf('api');
    if (apiIndex === -1 || parts.length <= apiIndex + 1) {
      return base;
    }
    return `/${parts.slice(apiIndex, apiIndex + 2).join('/')}`;
  }

  private headersToObject(headers: HttpHeaders): {[key: string]: string} {
    const result: {[key: string]: string} = {};
    headers.keys().forEach((key) => {
      const value = headers.get(key);
      if (value) {
        result[key] = value;
      }
    });
    return result;
  }

  private getUserCacheKey(): string {
    if (!this.tokenDataProvider.isLoggedIn()) {
      return 'anon';
    }
    const jwtUser = this.tokenDataProvider.getJwtUser();
    return jwtUser?.['@id'] || jwtUser?.id?.toString() || 'auth';
  }

  private buildOfflineError(req: HttpRequest<any>): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 0,
      statusText: 'OFFLINE',
      url: req.urlWithParams,
      error: {
        code: 'OFFLINE_CACHE_MISS',
        message: 'No cached response available while offline.'
      }
    });
  }
}
